'use client'

import { Fragment, useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check, Minus } from 'lucide-react'

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface PricingTier {
  /** Tier name (e.g. "Free", "Pro") */
  name: string
  /** Short supporting tagline */
  description?: string
  /** Headline price (e.g. "$0", "$20") */
  price: string
  /** Price unit/cadence shown after the price (e.g. "mo") */
  priceDescription?: string
  /** CTA button label */
  buttonText?: string
  /** CTA link target (used when `onButtonClick` is not provided) */
  buttonUrl?: string
  /** CTA button style */
  buttonVariant?: 'default' | 'outline'
  /** CTA click handler (takes precedence over `buttonUrl`) */
  onButtonClick?: () => void
}

interface PricingComparisonFeature {
  /** Feature/row label */
  feature: string
  /**
   * Cell values, one per tier, aligned to `tiers` by index.
   * `true`/`false` render a check / dash icon; strings render verbatim.
   */
  values: (string | boolean)[]
}

const defaultTiers: PricingTier[] = [
  { name: 'Free', description: 'Discover what Headlessly can do', price: '$0', priceDescription: 'mo', buttonText: 'Get Started', buttonVariant: 'outline' },
  { name: 'Grow', description: 'Designed for Agent-first teams', price: '$20', priceDescription: 'mo', buttonText: 'Get Started', buttonVariant: 'default' },
  { name: 'Scale', description: 'For large orgs that need full control', price: '$500', priceDescription: 'mo', buttonText: 'Get Started', buttonVariant: 'outline' },
]

const defaultFeatures: PricingComparisonFeature[] = [
  { feature: 'Credits', values: ['10 credits / mo', '100 credits / mo', '1,000 credits / mo'] },
  { feature: 'API Requests', values: ['10K / mo', '500K / mo', '10M / mo'] },
  { feature: 'Records', values: ['10K', '100K', '10M'] },
  { feature: 'Custom Domain', values: [false, '$10 /mo add-on', '$10 /mo add-on'] },
  { feature: 'Time Travel', values: [false, 'Private Beta', 'Private Beta'] },
  { feature: 'Leads / Data Sources', values: [false, 'Private Beta', 'Private Beta'] },
  { feature: 'SSO / SAML', values: [false, false, 'Private Beta'] },
  { feature: 'RBAC', values: [false, false, 'Private Beta'] },
]

export interface PricingMatrixProps {
  /** Section heading (omit/empty to hide the header block) */
  heading?: string
  /** Section description */
  description?: string
  /**
   * Pricing tiers rendered as connected cards with a comparison column each.
   * Fully dynamic — supply any number of tiers (typically 2–4).
   */
  tiers?: PricingTier[]
  /** Feature comparison rows; each row's `values` align to `tiers` by index. */
  features?: PricingComparisonFeature[]
  /** Additional CSS classes */
  className?: string
}

/**
 * PricingMatrix - Tiered pricing with a feature comparison table
 *
 * A connected row of plan cards above a feature×tier comparison grid. The tier
 * count is data-driven via the `tiers` prop, and the comparison matrix is keyed
 * by tier index via each feature's `values` array, so the same component renders
 * two, three, or more tiers from props alone.
 *
 * **Layout:**
 * ```
 * +--------------------------------------------------+
 * |                   Pricing                        |
 * |                 Description                       |
 * |  +-------------+-------------+-------------+      |
 * |  |   Tier 1    |   Tier 2    |   Tier 3    |      |
 * |  |     $0      |     $20     |    $500     |      |
 * |  +-------------+-------------+-------------+      |
 * |  +--------------------------------------------+  |
 * |  | Feature       Tier 1   Tier 2   Tier 3    |  |
 * |  | Credits       10/mo    100/mo   1,000/mo  |  |
 * |  +--------------------------------------------+  |
 * +--------------------------------------------------+
 * ```
 */
const PricingMatrix = ({
  heading = 'Start free. Scale with your agents.',
  description = 'Deploy autonomous services at any scale. No seats. No surprises.',
  tiers = defaultTiers,
  features = defaultFeatures,
  className,
}: PricingMatrixProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  const renderCellValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="size-5 text-primary" />
      ) : (
        <Minus className="size-5 text-muted-foreground" />
      )
    }
    if (value === 'Private Beta') {
      return <span className="italic text-xs text-muted-foreground">*Private Beta</span>
    }
    return value
  }

  // Connected-card edge treatment: collapse borders/radii between adjacent cards.
  const cardEdgeClass = (index: number, count: number) => {
    if (count <= 1) return ''
    if (index === 0) return 'lg:rounded-r-none lg:border-r-0'
    if (index === count - 1) return 'lg:rounded-l-none lg:border-l-0'
    return 'lg:rounded-none lg:border-x-0'
  }

  const showHeader = Boolean(heading || description)

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className={cn(tiers.length >= 3 ? 'max-w-6xl' : 'max-w-5xl', showHeader && 'container mx-auto')}>
        {showHeader && (
          <motion.div className="flex flex-col items-center gap-4 text-center" variants={headerAnimation}>
            {heading && <h2 className="text-3xl font-semibold lg:text-4xl">{heading}</h2>}
            {description && <Markdown size="lg">{description}</Markdown>}
          </motion.div>
        )}

        <motion.div
          className={cn('flex flex-col gap-6 lg:flex-row lg:gap-0', showHeader && 'mt-10')}
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {tiers.map((tier, index) => (
            <Fragment key={tier.name}>
              {index > 0 && <Separator orientation="vertical" className="hidden h-auto lg:block" />}
              <motion.div className="w-full" variants={itemAnimation}>
                <Card className={cn('flex h-full flex-col justify-evenly gap-8 text-center', cardEdgeClass(index, tiers.length))}>
                  <CardHeader>
                    <CardTitle>{tier.name}</CardTitle>
                    <p className="min-h-10 text-sm text-muted-foreground">{tier.description}</p>
                  </CardHeader>
                  <CardContent>
                    <span className="text-5xl font-base">{tier.price}</span>
                    {tier.priceDescription && <span className="text-sm text-muted-foreground">/{tier.priceDescription}</span>}
                  </CardContent>
                  <CardFooter>
                    {tier.onButtonClick ? (
                      <Button className="w-full dark:border-border cursor-pointer" variant={tier.buttonVariant} onClick={tier.onButtonClick}>
                        {tier.buttonText}
                      </Button>
                    ) : (
                      <Button className="w-full dark:border-border" variant={tier.buttonVariant} asChild>
                        <a href={tier.buttonUrl || '#'}>{tier.buttonText}</a>
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              </motion.div>
            </Fragment>
          ))}
        </motion.div>

        {features.length > 0 && (
          <motion.div className="mt-10 overflow-x-auto" variants={itemAnimation}>
            <Table className="min-w-[420px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead></TableHead>
                  {tiers.map((tier) => (
                    <TableHead key={tier.name} className="font-base text-primary">
                      {tier.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {features.map((row) => (
                  <TableRow key={row.feature} className="hover:bg-transparent">
                    <TableCell>{row.feature}</TableCell>
                    {tiers.map((tier, i) => (
                      <TableCell key={tier.name}>{renderCellValue(row.values[i])}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>
        )}
      </div>
    </motion.section>
  )
}

export { PricingMatrix }
export type { PricingComparisonFeature as PricingMatrixFeature, PricingTier as PricingMatrixPlan }
